/**
 * The store catalogue.
 *
 * ── The price ladder, and why these numbers ─────────────────────────────────
 *
 * A winning match pays ~150 coins (rewards.ts). Everything here is priced off
 * that single anchor, so a player can read a price and know roughly what it
 * costs in matches:
 *
 *   common     400   ~3 matches      an evening
 *   rare     1,200   ~8 matches      a few sessions
 *   epic     3,000   ~20 matches     a week or two
 *   legend   7,500   ~50 matches     a genuine goal
 *
 * Every celebration is buyable BOTH ways. That is a deliberate rule, not an
 * oversight: the moment something is cash-only, the store stops being a shop
 * and becomes a paywall, and the players who cannot pay — most of them — stop
 * reading it at all. Cash buys TIME, never access.
 *
 * ── Cash pricing ────────────────────────────────────────────────────────────
 *
 * In minor units (cents) because floating-point money is how rounding bugs
 * reach invoices. Ladder is $1.99 / $2.99 / $4.99 / $7.99, with coin packs
 * offering an increasing discount per coin so the bigger pack is honestly the
 * better deal rather than a trap.
 *
 * ── ids are permanent ───────────────────────────────────────────────────────
 *
 * A receipt references an item id forever. Renaming an item is fine; changing
 * or reusing its id orphans somebody's purchase. Treat this list as append-only.
 */

import type { CatalogueItem } from './types.js';

/**
 * Celebrations are the flagship product — the owner's stated one. `clipId`
 * points at the animation the pack will ship; nothing here plays yet, and the
 * store deliberately says so rather than selling a promise silently.
 */
export const CATALOGUE: readonly CatalogueItem[] = [
  /*
   * OWNER'S CATALOGUE (2026-08-09): boost animations at 5,000 and 10,000, a
   * celebration at 10,000. More to be added as the animation pack grows.
   *
   * ── "Boost" is a SKIN, not an ability ────────────────────────────────────
   *
   * The stamina boost is an existing gameplay feature every player already has.
   * These items change how it LOOKS — the sprint animation and its aura — and
   * grant no speed, no stamina and no advantage of any kind. That distinction
   * is the whole reason this is sellable: a football game that sells a faster
   * sprint loses the players who keep it alive, and `ItemKind` has no field
   * capable of expressing a gameplay effect precisely so the schema refuses.
   */
  {
    id: 'anim.boost1',
    kind: 'boost',
    name: 'Boost I Animation',
    blurb: 'A new look for the first boost tier. Cosmetic — the boost itself is unchanged.',
    rarity: 'epic',
    coinPrice: 5000,
    cashPriceMinor: 299,
    clipId: 'BOOST_T1',
  },
  {
    id: 'anim.boost2',
    kind: 'boost',
    name: 'Boost II Animation',
    blurb: 'The second tier, with its own sprint and aura. Cosmetic only.',
    rarity: 'legendary',
    coinPrice: 10000,
    cashPriceMinor: 499,
    clipId: 'BOOST_T2',
  },
  {
    id: 'anim.celebration',
    kind: 'celebration',
    name: 'Celebration Animation',
    blurb: 'A signature celebration, played when you score.',
    rarity: 'legendary',
    coinPrice: 10000,
    cashPriceMinor: 499,
    clipId: 'CEL_Signature',
  },

  // ── Coin packs ────────────────────────────────────────────────────────────
  // The only cash-only items, and the bigger packs are honestly better value
  // per coin rather than the usual trick of making the small one awful.
  {
    id: 'coins.small',
    kind: 'coins',
    name: '1,000 Coins',
    blurb: 'A start on your first animation.',
    rarity: 'common',
    coinPrice: null,
    cashPriceMinor: 199,
    grantsCoins: 1000,
  },
  {
    id: 'coins.medium',
    kind: 'coins',
    name: '5,000 Coins',
    blurb: 'Enough for Boost I outright.',
    rarity: 'rare',
    coinPrice: null,
    cashPriceMinor: 799,
    grantsCoins: 5000,
  },
  {
    id: 'coins.large',
    kind: 'coins',
    name: '12,000 Coins',
    blurb: 'Any single animation, with change.',
    rarity: 'epic',
    coinPrice: null,
    cashPriceMinor: 1699,
    grantsCoins: 12000,
  },
];

export function itemById(id: string): CatalogueItem | undefined {
  return CATALOGUE.find((i) => i.id === id);
}

export function itemsOfKind(kind: CatalogueItem['kind']): CatalogueItem[] {
  return CATALOGUE.filter((i) => i.kind === kind && !i.hidden);
}

/** Formatted cash price, e.g. 499 -> "$4.99". */
export function formatCash(minor: number): string {
  return `$${(minor / 100).toFixed(2)}`;
}

/** Formatted coin price with thousands separators. */
export function formatCoins(n: number): string {
  return n.toLocaleString('en-US');
}

export const RARITY_ORDER: Record<CatalogueItem['rarity'], number> = {
  common: 0, rare: 1, epic: 2, legendary: 3,
};
